import { memo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { updEventData } from "../../../ReduxSlice/Event/eventSlice";
import { buildEventMenu, getEventHubUrl, isProfileRoute } from "./eventMenuConfig";

const getPathname = (to) => (typeof to === "string" ? to : to?.pathname || "");

const getSearch = (to) => (typeof to === "string" ? "" : to?.search || "");

const EventV2SideMenu = ({
  activeId,
  userName,
  profileUser,
  onNavigate,
}) => {
  const { userDetails, masterToken } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const menu = buildEventMenu(userDetails, userName, profileUser);
  const onProfilePage = isProfileRoute(location.pathname);

  const handleExternalLink = (link, platform, e) => {
    if (!link) return;
    if (!platform) return;
    e.preventDefault();
    if (!masterToken) {
      window.location.href = link;
      return;
    }

    // Sidebar "More" keeps guest profile context when viewing another user.
    if (platform === "music") {
      if (userName) {
        window.location.href = `${link}/${userName}`;
      } else {
        window.location.href = `${link}/externalLogin?userName=${userDetails.userName}?authId=${masterToken}`;
      }
    } else if (platform === "class") {
      if (userName) {
        window.location.href = `${link}/auth/${masterToken}/?userName=${userName}`;
      } else {
        window.location.href = `${link}/auth/${masterToken}`;
      }
    } else if (platform === "social") {
      if (userName) {
        window.location.href = `${link}/u/${userName}`;
      } else {
        window.location.href = `${link}/user/auth/login?authId=${masterToken}`;
      }
    } else if (platform === "tv") {
      if (userName) {
        window.location.href = `${link}/@${userName}`;
      } else {
        window.location.href = `${link}/index.php?link1=external_auth&authId=${encodeURIComponent(masterToken)}`;
      }
    } else if (platform === "merch") {
      if (userName) {
        const merchBase = (
          process.env.REACT_APP_MERCH_URL || link || ""
        ).replace(/\/$/, "");
        window.location.href = `${merchBase}/auth/login/${masterToken}/${userName}`;
      } else {
        window.location.href = link;
      }
    } else {
      window.location.href = link;
    }
  };

  const handleItemClick = (item, e) => {
    const targetPath = getPathname(item.to);
    const targetSearch = getSearch(item.to);

    if (targetPath === "/myEvents/create/basic_info") {
      dispatch(updEventData({}));
    }

    // Soft client transitions from /:userName/userEvents often fail to leave
    // profile context. Force a full navigation for every hub destination.
    if (item.to && onProfilePage && !isProfileRoute(targetPath)) {
      e.preventDefault();
      window.location.assign(getEventHubUrl(targetPath, targetSearch));
      return;
    }

    if (onNavigate) onNavigate();
  };

  return (
    <div className="event-side-menu">
      {menu.map((section) => (
        <div key={section.label}>
          <div className="event-menu-section">{section.label}</div>
          <div className="event-side-menu-group" role="list">
            {section.items.map((item) => {
              const activeClass = item.id === activeId ? " is-active" : "";
              if (item.to) {
                const targetPath = getPathname(item.to);
                const targetSearch = getSearch(item.to);
                const leaveProfile =
                  onProfilePage && !isProfileRoute(targetPath);

                return (
                  <Link
                    key={item.id}
                    className={`event-side-link${activeClass}`}
                    to={
                      leaveProfile
                        ? { pathname: targetPath || "/", search: targetSearch }
                        : item.to
                    }
                    state={leaveProfile ? null : item.state}
                    role="listitem"
                    onClick={(e) => handleItemClick(item, e)}
                  >
                    <span className="event-side-link-icon" aria-hidden="true">
                      <i className={`bi ${item.icon}`}></i>
                    </span>
                    <span className="event-side-link-label">{item.label}</span>
                  </Link>
                );
              }
              return (
                <a
                  key={item.id}
                  className={`event-side-link${activeClass}`}
                  href={item.href || "#"}
                  role="listitem"
                  onClick={(e) => {
                    if (item.platform)
                      handleExternalLink(item.href, item.platform, e);
                    else if (item.href) window.location.href = item.href;
                    if (onNavigate) onNavigate();
                  }}
                >
                  <span className="event-side-link-icon" aria-hidden="true">
                    <i className={`bi ${item.icon}`}></i>
                  </span>
                  <span className="event-side-link-label">{item.label}</span>
                </a>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default memo(EventV2SideMenu);
